import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';

type DisplayType = 'text' | 'tags' | 'list' | 'date' | 'custom';

interface IDetailedItem {
  label?: string;
  text?: string | number;
  helpertext?: string;
  type?: DisplayType;
  tags?: string[];
  list?: string[] | { label: string; value: string }[];
  customRender?: () => React.ReactNode;
  tagClassName?: string;
  listClassName?: string;
}

export const DetailedItem = (props: IDetailedItem) => {
  const {
    label,
    text,
    helpertext,
    type = 'text',
    tags = [],
    list = [],
    customRender,
  } = props;

  const [showAllTags, setShowAllTags] = useState(false);

  const renderContent = () => {
    switch (type) {
      case 'tags': {
        const maxVisibleTags = 3;
        const visibleTags = showAllTags ? tags : tags.slice(0, maxVisibleTags);
        const remainingCount = tags.length - maxVisibleTags;
        const remainingTags = tags.slice(maxVisibleTags);

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {tags.length > 0 ? (
              <>
                {visibleTags.map((tag, idx) => (
                  <Chip
                    key={idx}
                    label={tag}
                    size="small"
                    sx={{
                      height: 26,
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      borderRadius: '9999px',
                      bgcolor: 'rgba(37, 99, 235, 0.06)',
                      color: 'primary.main',
                      border: '1px solid rgba(37, 99, 235, 0.15)',
                      '&:hover': {
                        bgcolor: 'rgba(37, 99, 235, 0.12)',
                      },
                    }}
                  />
                ))}
                {!showAllTags && remainingCount > 0 && (
                  <Chip
                    label={`+${remainingCount}`}
                    size="small"
                    clickable
                    title={remainingTags.join(', ')}
                    onClick={() => setShowAllTags(true)}
                    sx={{
                      height: 26,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderRadius: '9999px',
                      bgcolor: 'action.hover',
                      color: 'text.secondary',
                    }}
                  />
                )}
                {showAllTags && tags.length > maxVisibleTags && (
                  <Chip
                    label="Show less"
                    size="small"
                    clickable
                    onClick={() => setShowAllTags(false)}
                    sx={{
                      height: 26,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderRadius: '9999px',
                      bgcolor: 'action.hover',
                      color: 'text.secondary',
                    }}
                  />
                )}
              </>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                No tags
              </Typography>
            )}
          </Box>
        );
      }

      case 'list':
        return (
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {list.length > 0 ? (
              list.map((item, idx) => (
                <Box
                  component="li"
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    fontSize: '0.8125rem',
                    color: 'text.primary',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 700,
                      lineHeight: '1.2rem',
                    }}
                  >
                    •
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                    {typeof item === 'string' ? item : item.label}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                —
              </Typography>
            )}
          </Box>
        );

      case 'date': {
        const dateValue = text;
        let timestamp: Date;

        if (typeof dateValue === 'number') {
          timestamp =
            dateValue.toString().length === 10
              ? new Date(dateValue * 1000)
              : new Date(dateValue);
        } else if (typeof dateValue === 'string' && dateValue) {
          timestamp = new Date(dateValue);
        } else {
          timestamp = new Date();
        }

        return (
          <Typography sx={{ color: 'text.primary', fontSize: '0.8125rem', fontWeight: 500 }}>
            {timestamp.toLocaleDateString()}
          </Typography>
        );
      }

      case 'custom':
        return customRender ? customRender() : null;

      case 'text':
      default:
        if (helpertext) {
          return (
            <Tooltip title={helpertext} arrow placement="top">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                <Typography sx={{ color: 'text.primary', fontSize: '0.8125rem' }}>
                  {text}
                </Typography>
                <HelpOutlineRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              </Box>
            </Tooltip>
          );
        }
        return (
          <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', fontWeight: 500 }}>
            {text}
          </Typography>
        );
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {label && (
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.6875rem',
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {label}
        </Typography>
      )}
      {renderContent()}
    </Box>
  );
};

